// A seção Configuração: o fuso, o expediente e a regra que abre oportunidade. (#880)

import { useState, type ReactNode } from 'react';
import { AlterarARevenda, AlterarAIdentidade, AlterarOAutomatico } from './alterar-configuracao';
import { useQueryClient } from '@tanstack/react-query';
import { Estado } from '@/shared/ui/estados/estados';
import { useConfiguracao } from '@/shared/api/consultas';
import { chaves } from '@/shared/api/chaves';
import { type UsoDeStepUp } from '@/features/acesso/confirmar-identidade';
import { Bloco, Campo } from './partes-da-secao';

export function SecaoConfiguracao({ stepUp }: { stepUp: UsoDeStepUp }): ReactNode {
  const consulta = useConfiguracao();
  const invalidar = useQueryClient();
  const [alterando, definirAlterando] = useState<'revenda' | 'identidade' | 'automatico' | null>(
    null,
  );

  const salvo = () => {
    definirAlterando(null);
    // A árvore inteira: a regra de abertura decide o que é etapa aberta, e o
    // funil lê disso. Invalidar de menos numa tela de administração faz a
    // pessoa duvidar do que acabou de salvar.
    void invalidar.invalidateQueries({ queryKey: chaves.administracao() });
  };

  return (
    <Estado consulta={consulta} forma="ficha" linhas={4}>
      {(config) => (
        <section className="admin__secao" aria-label="Configuração">
          {/*
            Três blocos, agrupados pelo que DECIDEM — e não seis caixas iguais.

            Seis caixas com borda, fundo e raio davam a um fuso horário o mesmo
            peso visual de um cartão de conexão, e seis coisas com o mesmo peso
            não têm hierarquia nenhuma. Agrupadas, cada uma responde a uma
            pergunta diferente, e a última é a que mais custa quando está
            errada: são as duas regras que criam registro sem ninguém pedir.
          */}
          <Bloco
            titulo="A organização"
            apoio="É este fuso que datou cada mensagem do histórico, e este expediente que decide se a conversa entrou fora de hora."
            ultima={config.ultima_alteracao}
            aoAlterar={() => definirAlterando('revenda')}
          >
            <Campo rotulo="Fuso horário" valor={config.timezone} />
            <Campo
              rotulo="Expediente"
              valor={expedientePorExtenso(config.horario_de_atendimento)}
            />
          </Bloco>

          <Bloco
            titulo="Reconhecer a mesma pessoa"
            apoio="Os dois alimentam a mesma decisão: unir sozinho, ou mandar para a revisão de identidade."
            aoAlterar={() => definirAlterando('identidade')}
          >
            <Campo
              rotulo="Limiar para unir cadastros"
              valor={porcentagem(config.limiar_match_probabilistico)}
              apoio="Acima disto o sistema une sozinho; abaixo, manda para revisão."
            />
            <Campo
              rotulo="Documento na identidade"
              valor={config.usar_documento_na_identidade ? 'Em uso' : 'Fora de uso'}
              apoio="CPF e CNPJ como sinal para reconhecer a mesma pessoa."
            />
          </Bloco>

          <Bloco
            titulo="O que o sistema faz sozinho"
            apoio="As duas regras que criam registro sem ninguém pedir."
            aoAlterar={() => definirAlterando('automatico')}
          >
            <Campo
              rotulo="Abertura de oportunidade"
              valor={canaisDaRegra(config.criar_oportunidade_regra.canais)}
              apoio="Abre a partir de qualquer conversa nos canais acima."
            />
            <Campo
              rotulo="Ao encerrar conversa"
              valor={
                config.encerramento_regra.criar_tarefa_de_proxima_acao === true
                  ? 'Cria tarefa de próxima ação'
                  : 'Nenhuma tarefa é criada'
              }
            />
          </Bloco>

          {alterando === 'revenda' && (
            <AlterarARevenda
              config={config}
              stepUp={stepUp}
              aoFechar={() => definirAlterando(null)}
              aoSalvar={salvo}
            />
          )}
          {alterando === 'identidade' && (
            <AlterarAIdentidade
              config={config}
              stepUp={stepUp}
              aoFechar={() => definirAlterando(null)}
              aoSalvar={salvo}
            />
          )}
          {alterando === 'automatico' && (
            <AlterarOAutomatico
              config={config}
              stepUp={stepUp}
              aoFechar={() => definirAlterando(null)}
              aoSalvar={salvo}
            />
          )}
        </section>
      )}
    </Estado>
  );
}

// ── Conexões (D11) ─────────────────────────────────────────────────────────
const DIAS = ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado'] as const;
/**
 * O expediente em uma frase, e não sete linhas iguais.
 *
 * A revenda quase sempre abre no mesmo horário de segunda a sexta. Listar dia a
 * dia daria sete linhas repetidas e faria a exceção — o sábado até meio-dia —
 * se perder no meio delas.
 */
function expedientePorExtenso(
  janelas: readonly { diaDaSemana: number; abre: string; fecha: string }[],
): string {
  if (janelas.length === 0) return 'Não configurado';

  const porHorario = new Map<string, number[]>();
  for (const j of janelas) {
    const chave = `${j.abre}–${j.fecha}`;
    porHorario.set(chave, [...(porHorario.get(chave) ?? []), j.diaDaSemana]);
  }

  return [...porHorario.entries()]
    .map(([horario, dias]) => `${dias.map((d) => DIAS[d] ?? d).join(', ')}: ${horario}`)
    .join(' · ');
}

function porcentagem(fracao: number): string {
  return `${Math.round(fracao * 100)}%`;
}

function canaisDaRegra(canais: readonly string[] | undefined): string {
  if (!canais || canais.length === 0) return 'Todos os canais';
  return canais.join(', ');
}
